/**
 * Bound a promise. Nothing in this app awaits a native bridge without one.
 *
 * Three separate bugs in this project were "a promise that never settled":
 * the black screen (Preferences during boot), the update check (the App plugin
 * before the fetch), and every spinner that outlived its cause. None of them
 * threw. A hang is the worst failure mode there is, because it is
 * indistinguishable from work in progress and leaves nothing to read.
 *
 * `label` is not decoration — it is what turns "it's stuck" into "step 2 of 4
 * timed out", which is the difference between a fix and a guess.
 */

export class TimeoutError extends Error {
  constructor(label: string, ms: number) {
    super(`${label} timed out after ${ms}ms`)
    this.name = 'TimeoutError'
  }
}

export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms)
    promise.then(
      (value) => { clearTimeout(timer); resolve(value) },
      (err) => { clearTimeout(timer); reject(err) },
    )
  })
}

/** Timeout that resolves to a fallback instead of rejecting. */
export async function withTimeoutOr<T>(
  promise: Promise<T>,
  ms: number,
  fallback: T,
  label = 'operation',
): Promise<T> {
  try {
    return await withTimeout(promise, ms, label)
  } catch {
    return fallback
  }
}
